// @ts-nocheck
import React from 'react';
import { RecruitLookLogo } from '@/components/ui/logo';
import { useSubdomainBranding, OrgBranding } from '@/hooks/useSubdomainBranding';

interface PublicEventLogoProps {
  /** Branding object from a public function response (event -> organizations). */
  branding?: Partial<OrgBranding> | null;
  className?: string;
  /** Tailwind height class for the swapped org logo. */
  heightClass?: string;
}

/**
 * Renders the organizing org's logo for public pages when the event is
 * white-labeled, otherwise the default RecruitLook Events logo.
 *
 * Priority: explicit `branding` (from the page's function response) wins;
 * otherwise the site-wide subdomain branding is used as the default.
 * Colors/layout stay neutral — only the logo swaps.
 */
export const PublicEventLogo: React.FC<PublicEventLogoProps> = ({
  branding,
  className = '',
  heightClass = 'h-12',
}) => {
  const { branding: subdomainBranding } = useSubdomainBranding();
  const effective = branding ?? subdomainBranding;
  const [imageError, setImageError] = React.useState(false);

  const isWhiteLabel = effective && effective.is_recruitlook_branded === false;

  // Default RecruitLook branding.
  if (!isWhiteLabel) {
    return <RecruitLookLogo size="lg" className={className} />;
  }

  const name = effective.org_name || 'Event';
  const logoUrl = effective.logo_url;
  const website = effective.website;

  // White-label but no logo, or image failed to load: show org name as text.
  if (!logoUrl || imageError) {
    const text = (
      <span className={`text-xl font-bold text-foreground ${className}`}>{name}</span>
    );
    return website ? (
      <a href={website} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    ) : (
      text
    );
  }

  const img = (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      className={`${heightClass} w-auto object-contain ${className}`}
      onError={() => setImageError(true)}
    />
  );

  return website ? (
    <a href={website} target="_blank" rel="noopener noreferrer">
      {img}
    </a>
  ) : (
    img
  );
};

export default PublicEventLogo;
